import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Loader2,
  Sparkles,
  User,
  Brain,
  IndianRupee,
  CheckCircle2,
  AlertTriangle,
  CalendarClock,
  Plus,
  ArrowLeft,
  FileText,
  MapPin,
  TrendingUp,
  ExternalLink,
} from 'lucide-react';
import { Button } from './ui/button';
import IncomeBand from './IncomeBand';
import { getSchemeApplyLink } from '../utils/schemeApplyLinks';

/**
 * Read-only renderer for a past KMS Smart conversation.
 *
 * Fetches `/api/impact-analyser/analyses/{analysisId}` and paints the full
 * thread as a chat: the user's original profile text is shown as the first
 * user message, the AI's analysis report as the assistant reply, and any
 * follow-up Q&A pairs stored in `kms_training_data` are appended in order.
 *
 * No input box, no continue CTA — the only action is the "Start New Chat"
 * button at the bottom which calls `onStartNew()`.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

// -- Message bubbles -----------------------------------------------------

const UserBubble = ({ children }) => (
  <div className="flex items-start gap-3" data-testid="chat-bubble-user">
    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center">
      <User className="h-4 w-4 text-slate-600" />
    </div>
    <div className="flex-1 bg-slate-100 text-slate-900 rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap">
      {children}
    </div>
  </div>
);

const AiBubble = ({ children }) => (
  <div className="flex items-start gap-3" data-testid="chat-bubble-ai">
    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-emerald-600 flex items-center justify-center">
      <Brain className="h-4 w-4 text-white" />
    </div>
    <div className="flex-1 bg-white border border-emerald-100 rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed">
      {children}
    </div>
  </div>
);

// -- Analysis report renderer -------------------------------------------
// Compact inline card summarising everything the AI returned for the
// parent analysis. Purely presentational.

const AnalysisReportCard = ({ analysis }) => {
  const full = analysis?.full_ai_response || {};
  const schemes = Array.isArray(full.schemes) ? full.schemes : [];
  const benefit = analysis?.total_estimated_benefits || analysis?.total_benefit || '';
  const insight = full.insight || '';
  const headwinds = Array.isArray(full.headwinds) ? full.headwinds : [];
  const deadlines = Array.isArray(full.deadlines) ? full.deadlines : [];
  const actionStepsClean = Array.isArray(full.action_steps)
    ? full.action_steps
        .map((a) => (typeof a === 'string' ? a : a?.step || a?.title || ''))
        .filter((t) => t && t.trim())
    : [];

  return (
    <div className="space-y-4">
      {insight && (
        <p className="text-sm text-slate-800 leading-relaxed">
          <Sparkles className="inline h-3.5 w-3.5 text-emerald-600 mr-1.5 -mt-0.5" />
          {insight}
        </p>
      )}

      {benefit && (
        <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5">
          <IndianRupee className="h-4 w-4 text-emerald-700" />
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
              Estimated potential benefit
            </div>
            <div className="text-base font-bold text-emerald-900">{benefit}</div>
          </div>
        </div>
      )}

      {schemes.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 mb-2">
            Scheme analysis ({schemes.length})
          </div>
          <ul className="space-y-2">
            {schemes.slice(0, 8).map((s, i) => {
              const name = s.name || s.scheme_name || 'Scheme';
              const benefit = s.benefit_amount || s.benefit || s.amount || '';
              const why = s.why_you_qualify || s.why || '';
              const howTo = s.how_to_apply || s.apply || '';
              const docs = Array.isArray(s.documents_needed) ? s.documents_needed : [];
              const applyAt = s.apply_at || '';
              const effort = s.effort_level || '';
              return (
                <li
                  key={`${name}-${i}`}
                  className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-3"
                  data-testid={`scheme-analysis-${i}`}
                >
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="text-sm font-semibold text-slate-900">{name}</div>
                        {benefit && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[11px] font-semibold">
                            <IndianRupee className="h-3 w-3" />
                            {benefit}
                          </span>
                        )}
                      </div>
                      {why && (
                        <p className="mt-1.5 text-xs text-slate-700 leading-relaxed">
                          <span className="font-semibold text-slate-900">Why you qualify: </span>
                          {why}
                        </p>
                      )}
                      {howTo && (
                        <p className="mt-1 text-xs text-slate-700 leading-relaxed">
                          <span className="font-semibold text-slate-900">How to apply: </span>
                          {howTo}
                        </p>
                      )}
                      {docs.length > 0 && (
                        <div className="mt-1.5 flex items-start gap-1 text-xs text-slate-600">
                          <FileText className="h-3 w-3 text-slate-400 flex-shrink-0 mt-0.5" />
                          <span>
                            <span className="font-semibold text-slate-800">Docs: </span>
                            {docs.slice(0, 5).join(', ')}
                          </span>
                        </div>
                      )}
                      {(applyAt || effort) && (
                        <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500">
                          {applyAt && (
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {applyAt}
                            </span>
                          )}
                          {effort && (
                            <span className="inline-flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" />
                              {effort}
                            </span>
                          )}
                        </div>
                      )}
                      {(() => {
                        const link = getSchemeApplyLink(s);
                        if (!link) return null;
                        return (
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`scheme-apply-link-${i}`}
                            className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold px-2.5 py-1.5 transition-colors"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Apply on official portal
                          </a>
                        );
                      })()}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {actionStepsClean.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 mb-2">
            Action steps
          </div>
          <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700 pl-1">
            {actionStepsClean.slice(0, 6).map((t, i) => (
              <li key={i} className="leading-relaxed">
                {t}
              </li>
            ))}
          </ol>
        </div>
      )}

      {deadlines.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 mb-2 flex items-center gap-1.5">
            <CalendarClock className="h-3 w-3" />
            Deadlines to watch
          </div>
          <ul className="space-y-1 text-sm text-slate-700">
            {deadlines.slice(0, 4).map((d, i) => (
              <li key={i}>· {typeof d === 'string' ? d : d.label || d.deadline || ''}</li>
            ))}
          </ul>
        </div>
      )}

      {headwinds.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 mb-2 flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3" />
            Headwinds
          </div>
          <ul className="space-y-1 text-sm text-slate-700">
            {headwinds.slice(0, 4).map((h, i) => (
              <li key={i}>· {typeof h === 'string' ? h : h.label || h.title || ''}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// -- Main view ----------------------------------------------------------

const KmsChatHistoryView = ({ analysisId, onStartNew, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [data, setData] = useState(null);
  const [incomeBand, setIncomeBand] = useState(null);

  useEffect(() => {
    if (!analysisId) return;
    let cancelled = false;
    setLoading(true);
    setErr('');
    setIncomeBand(null);
    axios
      .get(`${API_URL}/api/impact-analyser/analyses/${analysisId}`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        // Kick off income-intelligence classification so the "Smart Tip"
        // band shows matching side-income ideas for this profile — same
        // endpoint and payload the live Results page uses.
        const profileText = res.data?.analysis?.profile_text;
        if (profileText) {
          axios
            .post(`${API_URL}/api/income-intelligence/classify-search`, {
              query: profileText,
              language: res.data?.analysis?.language || 'en',
            })
            .then((r) => {
              if (cancelled) return;
              if (r.data?.show_band && r.data?.income_band) {
                setIncomeBand(r.data.income_band);
              }
            })
            .catch(() => {
              /* silently fail — income band is optional */
            });
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setErr(
            e?.response?.status === 404
              ? 'This conversation could not be found.'
              : 'Failed to load conversation.'
          );
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [analysisId]);

  if (loading) {
    return (
      <div
        className="flex flex-col items-center justify-center py-24 text-slate-500"
        data-testid="chat-view-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin mb-3 text-emerald-600" />
        <span className="text-sm">Loading conversation…</span>
      </div>
    );
  }

  if (err) {
    return (
      <div
        className="bg-white border border-slate-200 rounded-2xl p-8 text-center"
        data-testid="chat-view-error"
      >
        <AlertTriangle className="h-6 w-6 text-amber-600 mx-auto mb-3" />
        <p className="text-sm text-slate-700 mb-5">{err}</p>
        <Button
          onClick={onStartNew}
          className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-5"
          data-testid="chat-view-error-new-chat"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Start new chat
        </Button>
      </div>
    );
  }

  const analysis = data?.analysis || {};
  const followups = Array.isArray(data?.followups) ? data.followups : [];
  const profileText = analysis.profile_text || '';

  return (
    <div
      className="flex flex-col bg-gradient-to-b from-slate-50 to-white rounded-2xl border border-slate-200 overflow-hidden"
      data-testid="kms-chat-history-view"
    >
      {/* Top meta bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="flex items-center gap-2 min-w-0">
          {onBack && (
            <button
              onClick={onBack}
              className="lg:hidden -ml-1 mr-1 rounded-full p-1.5 text-slate-500 hover:bg-slate-100"
              aria-label="Back to history list"
              data-testid="chat-view-back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <Sparkles className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
              Past conversation · read-only
            </div>
            <div className="text-xs text-slate-500 truncate">
              {formatDate(analysis.created_at)}
            </div>
          </div>
        </div>
      </div>

      {/* Thread */}
      <div
        className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 space-y-5 max-h-[70vh]"
        data-testid="chat-view-thread"
      >
        {/* Initial user profile */}
        {profileText && <UserBubble>{profileText}</UserBubble>}

        {/* Initial AI analysis report */}
        <AiBubble>
          <AnalysisReportCard analysis={analysis} />
        </AiBubble>

        {/* Income Intelligence — contextual "Smart Tip" band for side-income opportunities */}
        {incomeBand && (
          <div data-testid="history-income-band" className="pl-11 pr-1">
            <IncomeBand
              bandData={incomeBand}
              onDismiss={() => setIncomeBand(null)}
              onDismissPermanent={() => setIncomeBand(null)}
            />
          </div>
        )}

        {/* Follow-up Q&A chain */}
        {followups.map((f, i) => (
          <React.Fragment key={i}>
            {f.question && <UserBubble>{f.question}</UserBubble>}
            {f.answer && (
              <AiBubble>
                <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
                  {f.answer}
                </p>
              </AiBubble>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Bottom — Start New Chat only. No input. No continue. */}
      <div className="border-t border-slate-200 bg-white px-5 py-4 flex items-center justify-between gap-3">
        <div className="text-xs text-slate-400 hidden sm:block">
          This conversation is read-only.
        </div>
        <Button
          onClick={onStartNew}
          className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-5 py-2.5 font-semibold"
          data-testid="chat-view-start-new-chat"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Start new chat
        </Button>
      </div>
    </div>
  );
};

export default KmsChatHistoryView;
